import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import {
  SaleCenterClosingReviewer,
  SaleCenterClosingRoute,
  SaleCenterClosingSummary,
} from "../../types/sale-center-closing.type";
import { formatDateTimeBkk } from "../../utils/date";
import ClosingSectionBox from "./ClosingSectionBox";
import { CLOSING_STATUS_SHORT_LABEL } from "./saleCenterClosing.utils";

/** The three steps of a branch-day, in the order it actually moves through them. */
const BO_STEP_LABEL = "สาขา (BO) ตรวจสอบ";
const HO_STEP_LABEL = "สำนักงานใหญ่ (HO) ตรวจสอบ";
const CLOSING_STEP_LABEL = "ปิดศูนย์เซล";

const NOT_REVIEWED_LABEL = "ยังไม่ได้ตรวจสอบ";
const ALL_REVIEWED_LABEL = "ตรวจครบทุกสายส่ง";

/**
 * Names the sign-off as the LATEST one rather than the only one. Several people tick
 * different routes of the same branch-day, and an unqualified name under a part-checked
 * side is what read as "this side has finished with the day".
 */
const LATEST_REVIEWER_PREFIX = "ตรวจล่าสุดโดย";

/** Counted in สายส่ง, the unit the server states its own shortfall in, so the two agree. */
const partialProgressLabel = (reviewedCount: number, routeCount: number) =>
  `ตรวจแล้ว ${reviewedCount}/${routeCount} สายส่ง`;

/**
 * How many outstanding routes are named before the rest are only counted.
 *
 * A full branch runs about nine, and this box is one column of a two-column panel: naming
 * every route of an untouched branch-day would make the sign-offs the tallest thing on the
 * screen again, which is what the per-route table was removed for.
 */
const MAX_NAMED_WAITING_ROUTES = 4;

/**
 * The route has not remitted at all — `transfer_amount` is still NULL — so neither side can
 * sign it off yet. Worded as the server words the matching blocking reason
 * (แจ้งยอดโอนเข้าธนาคาร), so this line and a refusal that reaches the reader from the server
 * are recognisably about the same missing thing.
 */
const NO_TRANSFER_LABEL = "ยังไม่แจ้งยอดโอน";

/** What ปิดศูนย์เซล is still waiting for while the branch-day is open. */
const WAITING_FOR_SUPERVISOR_LABEL = "รอหัวหน้าแผนกบัญชีสำนักงานใหญ่ปิดศูนย์เซล";
const WAITING_FOR_DEPOSITS_LABEL = "ยังไม่มีรายการฝากเงินของวันนี้";
const WAITING_FOR_BO_LABEL = "รอสาขาตรวจสอบให้ครบ";
const WAITING_FOR_HO_LABEL = "รอสำนักงานใหญ่ตรวจสอบให้ครบ";
const WAITING_FOR_TRANSFER_LABEL = "รอสายส่งแจ้งยอดโอนให้ครบ";

/** How far one step has got: finished, moving, or not started. */
type ClosingStepTone = "complete" | "progress" | "pending";

/**
 * The tone is carried by the marker's fill AND by the words beside it, never by colour
 * alone — the box is read on projectors and by people who cannot separate the greens.
 */
const STEP_TONE_COLOR: Record<ClosingStepTone, string> = {
  complete: "success.main",
  progress: "warning.main",
  pending: "text.disabled",
};

const MARKER_SIZE = 12;
const MARKER_BORDER_WIDTH = 2;
const RAIL_WIDTH = 2;

/** The steps are an ordered list, and read as one to a screen reader; the bullets are ours. */
const TIMELINE_LIST_SX = { listStyle: "none", p: 0, m: 0 };

interface ClosingStep {
  label: string;
  /** Where the step stands, in one line. */
  state: string;
  tone: ClosingStepTone;
  /** Who signed last and when; empty on a step nobody has signed. */
  reviewerLine: string;
  /** Which routes the step is still waiting on; empty once nothing is outstanding. */
  waitingLine: string;
}

/** A side is only through when every route of the branch-day carries its tick. */
const isSideComplete = (reviewedCount: number, routeCount: number): boolean =>
  reviewedCount > 0 && reviewedCount >= routeCount;

/**
 * The routes one side has still to sign, named up to the cap and counted off after it.
 *
 * A route that has not remitted is marked as such: it is not merely unticked, it CANNOT be
 * ticked, and nothing else on the panel says so.
 */
const waitingRoutesLabel = (waitingRoutes: SaleCenterClosingRoute[]): string => {
  if (waitingRoutes.length === 0) return "";

  const named = waitingRoutes
    .slice(0, MAX_NAMED_WAITING_ROUTES)
    .map((route) =>
      route.has_transfer
        ? route.route_code
        : `${route.route_code} (${NO_TRANSFER_LABEL})`,
    )
    .join(", ");
  const unnamedCount = waitingRoutes.length - MAX_NAMED_WAITING_ROUTES;
  const rest = unnamedCount > 0 ? ` และอีก ${unnamedCount} สาย` : "";

  return `รออีก ${waitingRoutes.length} สาย · ${named}${rest}`;
};

const reviewerLabel = (reviewer: SaleCenterClosingReviewer | null): string => {
  if (!reviewer) return "";
  // Bangkok wall-clock in the same DD/MM/YYYY HH:mm shape as the วัน เวลา การโอน column, so
  // a sign-off and the transfer it signs off read on one clock.
  const reviewedAt = formatDateTimeBkk(reviewer.reviewed_at);
  const stamp = reviewedAt ? ` · ${reviewedAt} น.` : "";
  return `${LATEST_REVIEWER_PREFIX} ${reviewer.name}${stamp}`;
};

interface ReviewStepInput {
  label: string;
  /** Latest ตรวจแล้ว on this side — several people may have ticked different routes. */
  reviewer: SaleCenterClosingReviewer | null;
  /** Deposits on this side that carry the tick, against the branch-day's สายส่ง count. */
  reviewedCount: number;
  routeCount: number;
  /** The branch-day's routes this side has not signed off yet. */
  waitingRoutes: SaleCenterClosingRoute[];
}

/**
 * One side of the sign-off: how far it has got, who signed last, and what is left.
 *
 * Progress leads because it is the question being asked — a name on a day where one route of
 * three is ticked reads as "this side is done". An untouched side says so in words rather
 * than leaving a gap, which would read as "the name did not load".
 */
const reviewStep = ({
  label,
  reviewer,
  reviewedCount,
  routeCount,
  waitingRoutes,
}: ReviewStepInput): ClosingStep => {
  const isComplete = isSideComplete(reviewedCount, routeCount);
  const hasStarted = reviewedCount > 0;

  const stateOf = (): string => {
    if (!hasStarted) return NOT_REVIEWED_LABEL;
    return isComplete
      ? ALL_REVIEWED_LABEL
      : partialProgressLabel(reviewedCount, routeCount);
  };

  const toneOf = (): ClosingStepTone => {
    if (isComplete) return "complete";
    return hasStarted ? "progress" : "pending";
  };

  return {
    label,
    state: stateOf(),
    tone: toneOf(),
    reviewerLine: hasStarted ? reviewerLabel(reviewer) : "",
    waitingLine: isComplete ? "" : waitingRoutesLabel(waitingRoutes),
  };
};

/**
 * What the closing itself is still waiting for, in the order the day actually moves.
 *
 * `can_close` is asked first and is the server's own answer: the step never says the day is
 * waiting on the supervisor while the API would still refuse her.
 */
const openClosingStateLabel = (summary: SaleCenterClosingSummary): string => {
  if (summary.can_close) return WAITING_FOR_SUPERVISOR_LABEL;
  if (summary.route_count === 0) return WAITING_FOR_DEPOSITS_LABEL;
  if (!isSideComplete(summary.bo_reviewed_count, summary.route_count)) {
    return WAITING_FOR_BO_LABEL;
  }
  if (!isSideComplete(summary.ho_reviewed_count, summary.route_count)) {
    return WAITING_FOR_HO_LABEL;
  }
  // Both sides are through and the server still refuses: the only shortfall left that this
  // summary can name is a route that never remitted. Rarer refusals — a day somebody else
  // closed first — reach the reader as the server's error on the next attempt, not here.
  return WAITING_FOR_TRANSFER_LABEL;
};

/**
 * The closing itself — what is holding it up, or that it has happened.
 *
 * A settled day takes the SHORT status wording: the step is already labelled ปิดศูนย์เซล, so
 * the spelled-out ปิดศูนย์เซลแล้ว underneath it stutters, and it is word-for-word the panel
 * header's own status chip a few lines above.
 *
 * It deliberately says nothing about who froze the day or when: ผลการปิดศูนย์เซล prints
 * exactly that in the column beside this one, and the same fact twice side by side reads as
 * a bug rather than as confirmation.
 */
const closingStep = (summary: SaleCenterClosingSummary): ClosingStep => {
  const step = { label: CLOSING_STEP_LABEL, reviewerLine: "", waitingLine: "" };

  if (summary.status === "closed") {
    return {
      ...step,
      state: CLOSING_STATUS_SHORT_LABEL.closed,
      tone: "complete",
    };
  }
  // Sent back is a decision that has been taken on the day without finishing it, so the
  // marker is filled like a step in progress rather than left as one nobody has reached.
  if (summary.status === "sent_back") {
    return {
      ...step,
      state: CLOSING_STATUS_SHORT_LABEL.sent_back,
      tone: "progress",
    };
  }
  return { ...step, state: openClosingStateLabel(summary), tone: "pending" };
};

interface ClosingTimelineStepProps {
  step: ClosingStep;
  /** The last step has nothing to join to, so the rail stops at its marker. */
  isLast: boolean;
}

const ClosingTimelineStep = ({ step, isLast }: ClosingTimelineStepProps) => {
  const toneColor = STEP_TONE_COLOR[step.tone];

  return (
    <Stack component="li" direction="row" gap={1.5}>
      {/* The rail: a dot filled once the step has moved at all, hollow before that, joined by
          a line down to the next one. Drawn rather than iconised — a tick or a ring in a
          read-only box beside a table of live checkboxes is what the branch tried to click.
          Hidden from screen readers, which are given the same states in the words beside it. */}
      <Stack alignItems="center" aria-hidden>
        <Box
          sx={{
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            mt: 0.5,
            borderRadius: "50%",
            border: MARKER_BORDER_WIDTH,
            borderColor: toneColor,
            bgcolor: step.tone === "pending" ? "transparent" : toneColor,
          }}
        />
        {!isLast && (
          <Box sx={{ width: RAIL_WIDTH, flexGrow: 1, bgcolor: "divider" }} />
        )}
      </Stack>
      {/* The gap between steps is the content's own padding, so the rail runs unbroken. */}
      <Stack sx={{ minWidth: 0, pb: isLast ? 0 : 2 }}>
        <Typography variant="body2" fontWeight={600}>
          {step.label}
        </Typography>
        <Typography variant="body2" fontWeight={700} color={toneColor}>
          {step.state}
        </Typography>
        {step.reviewerLine && (
          <Typography variant="caption" color="text.secondary">
            {step.reviewerLine}
          </Typography>
        )}
        {/* Kept quiet: half the routes are outstanding for most of the day, and a list that
            shouts at every unfinished line stops being read. The state above it is the
            coloured part. */}
        {step.waitingLine && (
          <Typography variant="caption" color="text.secondary">
            {step.waitingLine}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

interface ClosingReviewerBoxProps {
  summary: SaleCenterClosingSummary;
}

/**
 * How far this branch-day has got, as one three-step timeline: สาขา (BO) ตรวจสอบ,
 * สำนักงานใหญ่ (HO) ตรวจสอบ, ปิดศูนย์เซล.
 *
 * Read-only for EVERY role, the supervisor included: the two ticks are made in the report
 * table above, never here. It replaced a per-route ✔/○ table which — sitting beside a table
 * whose own checkboxes ARE clickable — read as something to tick.
 *
 * Each step answers the question the one before it raises: how much of the day is verified,
 * then which routes are holding that up, and whether one of them has even remitted yet. The
 * closing step then says what the whole sequence is waiting for, which is the answer the
 * branch back office comes to this panel for.
 */
export default function ClosingReviewerBox({
  summary,
}: ClosingReviewerBoxProps) {
  const steps: ClosingStep[] = [
    reviewStep({
      label: BO_STEP_LABEL,
      reviewer: summary.bo_reviewer,
      reviewedCount: summary.bo_reviewed_count,
      routeCount: summary.route_count,
      waitingRoutes: summary.routes.filter((route) => !route.bo_reviewed),
    }),
    reviewStep({
      label: HO_STEP_LABEL,
      reviewer: summary.ho_reviewer,
      reviewedCount: summary.ho_reviewed_count,
      routeCount: summary.route_count,
      waitingRoutes: summary.routes.filter((route) => !route.ho_reviewed),
    }),
    closingStep(summary),
  ];

  return (
    <ClosingSectionBox title="ผู้ตรวจสอบ" icon={NoteAltOutlinedIcon} accent="success">
      <Stack component="ol" sx={TIMELINE_LIST_SX}>
        {steps.map((step, index) => (
          <ClosingTimelineStep
            key={step.label}
            step={step}
            isLast={index === steps.length - 1}
          />
        ))}
      </Stack>
    </ClosingSectionBox>
  );
}
